import { useContext, useEffect, useState } from 'react';
import { Box, Drawer, Theme, Tooltip, useTheme } from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { Region, Country } from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';
import { DataGrid, GridActionsCellItem, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import {
  RegionData,
  allowDeleteRegion,
  createRegions,
  deleteRegion,
  getRegions,
  region_label,
  updateRegion
} from '../../../lib/metric_capture/region';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import ConfigAddWidget, { CreateInput } from '../ConfigAddWidget';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { PanelTabs, PanelTabsWrapper } from '../../shared/tab/PanelTabsWrapper';
import ConfigEditWidget, { EditInput } from '../ConfigEditWidget';
import { BatchWrite } from '../../../lib/app/db_util';
import CountrySelect from '../../shared/input/select/CountrySelect';
import { Heading } from '../../shared/typography/PrimaryTypography';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

interface PanelRegionsProps {
  master_list?: boolean;
}

const SOURCE = 'PanelRegions.tsx';

/**
 * Datagrid handling the display of configured or Master List Regions
 * @returns {JSX.Element}
 */
const PanelRegions = ({ master_list }: PanelRegionsProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const [actionRow, setActionRow] = useState<Region | null>(null);
  const [isCreateWidget, setIsCreateWidget] = useState<boolean>(true);
  const [regionRows, setRegionRows] = useState<Array<Region>>([]);
  const [displayWidgetPanelRight, setDisplayWidgetPanelRight] = useState<boolean>(false);
  const [openDialogDelete, setOpenDialogDelete] = useState<boolean>(false);
  const [allowRegionDelete, setAllowRegionDelete] = useState<boolean | null>(
    master_list ? true : null
  );
  const [gridLoading, setGridLoading] = useState<boolean>(true);
  const theme: Theme = useTheme();

  const countryInputFactory = (
    handleInputChange: (input_id: string, value: Country | null) => void
  ): JSX.Element => {
    return (
      <CountrySelect
        master_list
        handleChangeCountries={(country: Array<Country> | Country | null) => {
          handleInputChange('country', country as Country | null);
        }}
      />
    );
  };

  // Handler functions
  const handleCreateClick = (): void => {
    setActionRow(null);
    setIsCreateWidget(true);
    setDisplayWidgetPanelRight(true);
  };

  const handleEditClick = (region: Region): void => {
    setActionRow(region);
    setIsCreateWidget(false);
    setDisplayWidgetPanelRight(true);
  };

  const handleDeleteClick = async (row: Region): Promise<void> => {
    try {
      if (!master_list && group && company) {
        setAllowRegionDelete(await allowDeleteRegion(group.id, company.id, row.id));
      }
      setActionRow(row);
      setOpenDialogDelete(true);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleDeleteClick'),
          {
            group: group,
            company: company,
            row: row
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete region. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const handleUpdateRows = (rows_data: Array<Region>): void => {
    setRegionRows(rows_data);
  };

  const handleCloseDeleteModal = (): void => {
    setOpenDialogDelete(false);
  };

  const columns: Array<GridColDef> = [
    {
      field: 'country',
      headerName: 'Country',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return params.value.name;
      },
      flex: 1
    },
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            {master_list && (
              <GridActionsCellItem
                key={1}
                icon={
                  <Tooltip title="Edit Region">
                    <EditIcon width={25} fill={theme.palette.primary.main} />
                  </Tooltip>
                }
                size="large"
                label="Edit"
                sx={{
                  color: 'primary.main'
                }}
                onClick={() => handleEditClick(row)}
                disabled={row.locked}
              />
            )}
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete Region">
                  <DeleteIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
            />
          </>
        ];
      }
    }
  ];

  const create_inputs: readonly CreateInput[] = master_list
    ? [
        {
          id: 'country',
          type: 'node',
          label: 'Country',
          inputNodeFactory: countryInputFactory
        },
        {
          id: 'name',
          type: 'text',
          label: 'Name'
        }
      ]
    : [];

  const edit_inputs: EditInput[] = master_list
    ? [
        {
          id: 'name',
          type: 'text',
          label: 'Name'
        }
      ]
    : [];

  // Row functions
  const fetchRows = async (): Promise<void> => {
    setGridLoading(true);
    try {
      // Load rows from database.
      const regions: Array<Region> = await getRegions(!!master_list, group?.id, company?.id);
      // Load rows to memory.
      setRegionRows(regions);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchRows'),
          {
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch regions. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setGridLoading(false);
    }
  };

  useEffect(
    () => {
      try {
        (async () => {
          setRegionRows([]);
          fetchRows();
        })().catch((error) => {
          throw new Error(error);
        });
      } catch (err: unknown) {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE),
            null,
            tracking_id
          )
        );
        setFeedbackData({
          message: `An error occurred. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      }
      return;
    },
    master_list ? [] : [group, company]
  );

  const createRowsFromMasterList = async (region_list: Array<Region>): Promise<void> => {
    try {
      if (!master_list && group && company) {
        const new_region_writes: Array<BatchWrite> = await createRegions(
          false,
          region_list,
          group.id,
          company.id
        );
        if (new_region_writes) {
          handleUpdateRows([
            ...new_region_writes.map((new_region_write: BatchWrite) => {
              return {
                id: new_region_write.id,
                modified: new_region_write.data.modified,
                deleted: new_region_write.data.deleted,
                name: new_region_write.data.name,
                country: new_region_write.data.country
              };
            }),
            ...regionRows
          ]);
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createRowsFromMasterList'),
          {
            group: group,
            company: company,
            region_list: region_list
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to create regions. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const createRow = async (name: string, country: Country): Promise<void> => {
    try {
      const region_data: RegionData = {
        modified: new Date(),
        deleted: null,
        name: name,
        country: country
      };
      const new_region_writes: Array<BatchWrite> = await createRegions(
        !!master_list,
        [region_data],
        undefined,
        undefined,
        true
      );
      if (new_region_writes) {
        handleUpdateRows([
          ...new_region_writes.map((new_region_write: BatchWrite) => {
            return {
              id: new_region_write.id,
              modified: new_region_write.data.modified,
              deleted: new_region_write.data.deleted,
              name: new_region_write.data.name,
              country: country
            };
          }),
          ...regionRows
        ]);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createRow'),
          {
            name: name,
            country: country
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to create regions. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const updateRow = async (updated_row: Region, original_data: Region): Promise<void> => {
    try {
      await updateRegion(!!master_list, updated_row, original_data, group?.id, company?.id).then(
        () => {
          handleUpdateRows(
            regionRows.map((row: Region) => (updated_row.id === row.id ? { ...updated_row } : row))
          );
        }
      );
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateRow'),
          {
            master_list: master_list,
            group: group,
            company: company,
            updated_row: updated_row,
            original_data: original_data
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update region. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const deleteRow = async (region: Region): Promise<void> => {
    try {
      await deleteRegion(!!master_list, region.id, region.name, group?.id, company?.id).then(() => {
        setRegionRows(
          regionRows.filter((row: Region) => {
            return row.id !== region.id;
          })
        );
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteRow'),
          {
            group: group,
            company: company,
            region: region
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to remove region. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setOpenDialogDelete(false);
    }
  };

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={openDialogDelete}
        allow_delete={allowRegionDelete}
        handleCloseDeleteModal={handleCloseDeleteModal}
        delete_label={actionRow && actionRow.name}
        handleDelete={actionRow && (() => deleteRow(actionRow))}
        allow_archive={false}
      />

      {/* Side Widget Panel */}
      <Drawer
        anchor={'right'}
        open={displayWidgetPanelRight}
        onClose={() => setDisplayWidgetPanelRight(false)}
        PaperProps={{ style: { width: '30%', padding: '1.5rem' } }}
      >
        {/* Create / Edit Widget */}
        {isCreateWidget ? (
          <>
            {master_list ? (
              <ConfigAddWidget
                widget_label={region_label.one}
                icon={
                  <LocationOnOutlinedIcon
                    color="primary"
                    sx={{ marginRight: '2rem' }}
                    fontSize="large"
                  />
                }
                inputs={create_inputs}
                handleClose={() => setDisplayWidgetPanelRight(false)}
                createFunction={(country: Country, name: string) => createRow(name, country)}
              />
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}
                >
                  <LocationOnOutlinedIcon
                    color="primary"
                    sx={{ marginRight: '2rem' }}
                    fontSize="large"
                  />
                  <Heading>Add {region_label.one}</Heading>
                </Box>
                <PanelTabsWrapper>
                  <PanelTabs label="Generic" disabled={false}>
                    <ConfigAddWidget
                      widget_label={region_label.one}
                      icon={
                        <LocationOnOutlinedIcon sx={{ marginRight: '2rem' }} fontSize="large" />
                      }
                      inputs={create_inputs}
                      existing_entity_labels={regionRows.map((region: Region) => region.name)}
                      handleClose={() => setDisplayWidgetPanelRight(false)}
                      createFunction={(region_list: Array<Region>) =>
                        createRowsFromMasterList(region_list)
                      }
                    />
                  </PanelTabs>
                  <PanelTabs label={'Custom'} disabled={true} />
                </PanelTabsWrapper>
              </>
            )}
          </>
        ) : (
          <ConfigEditWidget
            widget_label={region_label.one}
            icon={
              <LocationOnOutlinedIcon
                color="primary"
                sx={{ marginRight: '2rem' }}
                fontSize="large"
              />
            }
            handleClose={() => setDisplayWidgetPanelRight(false)}
            edit_entity={actionRow}
            inputs={edit_inputs}
            handleEditInput={(input_id: string, value: unknown) =>
              actionRow && setActionRow({ ...actionRow, [input_id]: value })
            }
            confirmEditFunction={(updated_region: Region, original_region: Region) =>
              updateRow(updated_region, original_region)
            }
          />
        )}
      </Drawer>

      {/* Interactive Data Table */}
      <DataGrid
        autoHeight
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          },
          sorting: {
            sortModel: [{ field: 'modified', sort: 'desc' }]
          }
        }}
        hideFooter={regionRows.length > 1 ? false : true}
        columnHeaderHeight={regionRows.length < 1 ? 0 : 56}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={gridLoading}
        rows={regionRows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => (
            <DataGridToolbar
              entity_label={region_label}
              disable_export={regionRows.length === 0}
              handleCreate={handleCreateClick}
            />
          ),
          noRowsOverlay: () => <DataGridNoRows missing_label={region_label} />,
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        slotProps={{ toolbar: { handleCreate: handleCreateClick } }}
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
      />
    </>
  );
};

export default PanelRegions;
